import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, RunRow } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

export function Task() {
  const { taskId = "" } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["task-runs", taskId],
    queryFn: () =>
      api<RunRow[]>(`/api/tasks/${encodeURIComponent(taskId)}/runs`),
    refetchInterval: 3_000,
    enabled: !!taskId,
  });

  if (isLoading) {
    return <p className="text-sm text-slate-500">Loading runs…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-rose-700">
        Failed to load runs: {(error as Error).message}
      </p>
    );
  }
  if (!data) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2">
        <Link
          to="/"
          className="text-xs text-slate-500 hover:underline"
        >
          ← all tasks
        </Link>
      </div>
      <h1 className="text-lg font-semibold">
        Task: <span className="font-mono">{taskId}</span>
      </h1>
      <p className="text-xs text-slate-500">
        {data.length} run{data.length === 1 ? "" : "s"}
      </p>
      <div className="overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Agent</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Duration</th>
              <th className="text-right px-4 py-2 font-medium">Tokens</th>
              <th className="text-right px-4 py-2 font-medium">Started</th>
              <th className="text-left px-4 py-2 font-medium">Run</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r) => (
              <tr
                key={r.id}
                className="border-t border-slate-100 hover:bg-slate-50"
              >
                <td className="px-4 py-2 font-mono">{r.agent_name}</td>
                <td className="px-4 py-2">
                  <StatusBadge status={r.status} />
                </td>
                <td className="text-right px-4 py-2 tabular-nums">
                  {duration(r.started_at, r.ended_at)}
                </td>
                <td className="text-right px-4 py-2 tabular-nums">
                  {totalTokens(r)}
                </td>
                <td className="text-right px-4 py-2 text-xs text-slate-500">
                  {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2">
                  <Link
                    to={`/runs/${r.id}`}
                    className="text-blue-700 hover:underline font-mono text-xs"
                  >
                    {r.id.slice(0, 8)}…
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function duration(a: string | null, b: string | null): string {
  if (!a) return "—";
  const start = +new Date(a);
  const end = b ? +new Date(b) : Date.now();
  const ms = Math.max(0, end - start);
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function totalTokens(r: RunRow): string {
  if (!r.usage) return "—";
  const i = r.usage.input_tokens ?? 0;
  const o = r.usage.output_tokens ?? 0;
  return `${(i + o).toLocaleString()}`;
}
